/**
 * MONSTER TOWN — VENUE HARNESS EXTRAS. OWNED BY THE VENUE CREW.
 *
 * Adds named angles, spots and demo layouts so `npm run shot`, `npm run drive`
 * and the critic loops can see the shop actually being run. Registered on
 * `app:booted` ON PURPOSE: `installHarness()` runs after every crew installs and
 * re-registers the placeholder `venue` angle, so ours has to land afterwards to
 * win. We never change or remove an existing harness name — we replace the
 * placeholder with the real thing and add new ones alongside.
 */

import { bus } from '../core/events'
import { engine } from '../core/engine'
import { wallet } from '../core/economy'
import { world } from '../core/scene'
import type { SceneId } from '../core/types'
import { registerAngle, registerDemo, registerSpot, settle } from '../harness'
import { TOWN_SHOP_SCENE } from '../content/venue'
import { debugBuyCheapest, restockShop, shopModel } from '../economy/shop'
import { seedWorld } from '../economy/index'
import { startJob, activeJobMarks, dropFind } from '../economy/jobs'
import { JOB_BOARD_CELL } from '../content/venue'
import { roomCenter } from './scene'
import {
  crowdClearLog,
  crowdFor,
  currentTray,
  debugServeOne,
  debugStageService,
  totalServed,
} from './service'
import { allVenues } from './types'

const TOWN = 'town' as SceneId

export interface VenueHarnessApi {
  stage(sceneId?: string): number
  serve(correct: boolean, sceneId?: string): 'correct' | 'wrong' | 'none'
  waiting(sceneId?: string): number
  customers(sceneId?: string): number
  clears(): { count: number; last: string }
  /** Phase + live animation action per customer. "Are they actually sitting?" */
  poses(sceneId?: string): { phase: string; action: string | null; y: number; shape: string }[]
  order(sceneId?: string): { base: string; scoops: string[]; topping: string | null } | null
  tray(): { base: string | null; scoops: string[]; topping: string | null }
  served(): number
  coins(): number
  shop(): { defId: string; cost: number; owned: boolean; affordable: boolean }[]
  buyCheapest(): { ok: boolean; defId: string | null; cost: number }
  job(): number
  jobMarks(): number
  drop(): boolean
  seed(): void
  /** Debug: pause/resume this crew's engine systems, to attribute frame cost. */
  systems(on: boolean): string[]
}

declare global {
  interface Window {
    __venue?: VenueHarnessApi
  }
}

function firstVenueScene(): SceneId {
  return allVenues()[0]?.sceneId ?? ('venue1' as SceneId)
}

export function installVenueHarness(): void {
  bus.on('app:booted', () => {
    const venueScene = firstVenueScene()
    const vc = roomCenter(venueScene)
    const sc = roomCenter(TOWN_SHOP_SCENE)

    // NOTE: `venue` belongs to the world crew — it frames their shopfront out
    // in the town. These are the INSIDE views, where the job actually happens.
    // Zoom indices are now RELATIVE TO THE ROOM (see `frameRoom` in index.ts):
    // 2 is the whole room, 1 is the working view where faces and food read, 0
    // leans in on one table. The headline angles sit at 1 on purpose.
    registerAngle({ name: 'venue-inside', sceneId: venueScene, target: vc, rot: 0, zoom: 1 })
    registerAngle({ name: 'venue-inside-close', sceneId: venueScene, target: vc, rot: 0, zoom: 0 })
    registerAngle({ name: 'venue-inside-back', sceneId: venueScene, target: vc, rot: 2, zoom: 1 })
    registerAngle({ name: 'shop-inside', sceneId: TOWN_SHOP_SCENE, target: sc, rot: 0, zoom: 1 })
    const rest = allVenues()[1]
    if (rest !== undefined) {
      registerAngle({
        name: 'restaurant-inside',
        sceneId: rest.sceneId,
        target: roomCenter(rest.sceneId),
        rot: 0,
        zoom: 1,
      })
    }

    registerSpot('venue-inside', venueScene, vc)
    registerSpot('shop-inside', TOWN_SHOP_SCENE, sc)
    const board = world.get(TOWN)?.grid.cellToWorld(JOB_BOARD_CELL) ?? { x: 0, y: 0, z: 0 }
    registerAngle({ name: 'town-jobs', sceneId: TOWN, target: board, rot: 0, zoom: 1 })
    registerSpot('job-board', TOWN, { x: board.x + 2, y: 0, z: board.z + 2 })

    // The player monster's render system calls `camera.lookAtPoint(playerPos)`
    // EVERY frame, so a named angle alone cannot frame an interior — the camera
    // snaps straight back to wherever the monster is standing. Every demo below
    // therefore moves the monster into the room first. (Same reason the real
    // game emits `play:walkTo` on entering a room.)
    const goTo = (spot: string): void => {
      window.__harness?.teleport(spot)
    }

    registerDemo('venue', async () => {
      seedWorld(true)
      world.setActiveImmediate(venueScene)
      goTo('venue-inside')
      // Let the scene-change beat land before staging: entering a venue clears
      // it (customers live in the never-saved `actors` layer and walk back in),
      // and the interiors crew synthesises `scene:entered` for harness switches.
      await settle(4)
      const n = debugStageService(venueScene)
      await settle(3)
      return n
    })

    registerDemo('restaurant', async () => {
      const r = allVenues()[1]
      if (r === undefined) return 0
      seedWorld(true)
      world.setActiveImmediate(r.sceneId)
      registerSpot('restaurant-inside', r.sceneId, roomCenter(r.sceneId))
      goTo('restaurant-inside')
      await settle(4)
      const n = debugStageService(r.sceneId)
      await settle(3)
      return n
    })

    registerDemo('shop', async () => {
      seedWorld(true)
      world.setActiveImmediate(TOWN_SHOP_SCENE)
      goTo('shop-inside')
      await settle(4)
      restockShop()
      await settle(3)
      return world.require(TOWN_SHOP_SCENE).grid.count()
    })

    registerDemo('jobs', async () => {
      seedWorld(true)
      world.setActiveImmediate(TOWN)
      goTo('job-board')
      await settle(3)
      const n = startJob(TOWN)
      await settle(3)
      return n
    })

    window.__venue = {
      stage: (sceneId = venueScene) => debugStageService(sceneId as SceneId),
      serve: (correct, sceneId = venueScene) => debugServeOne(sceneId as SceneId, correct),
      waiting: (sceneId = venueScene) => crowdFor(sceneId as SceneId)?.waitingCount() ?? 0,
      customers: (sceneId = venueScene) => crowdFor(sceneId as SceneId)?.count() ?? 0,
      clears: () => crowdClearLog(),
      poses: (sceneId = venueScene) => crowdFor(sceneId as SceneId)?.debugPoses() ?? [],
      order: (sceneId = venueScene) => {
        const w = crowdFor(sceneId as SceneId)?.anyWaiting() ?? null
        return w === null ? null : { base: w.order.base, scoops: [...w.order.scoops], topping: w.order.topping }
      },
      tray: () => currentTray(),
      served: () => totalServed(),
      coins: () => wallet.coins,
      shop: () => shopModel().map((e) => ({ ...e, defId: String(e.defId) })),
      buyCheapest: () => {
        const r = debugBuyCheapest()
        return { ok: r.ok, defId: r.defId === null ? null : String(r.defId), cost: r.cost }
      },
      job: () => startJob(TOWN),
      jobMarks: () => activeJobMarks(),
      drop: () => dropFind(TOWN),
      seed: () => seedWorld(true),
      systems: (on) => {
        const ids = ['venue.walls', 'venue.service', 'economy.town']
        for (const sid of ids) engine.setPaused(sid, !on)
        return ids
      },
    }
  })
}
